import React from 'react';
import PropTypes from 'prop-types';
import MuiIcons from '../../utils/MuiIcons';

/**
 * @typedef {import('../../types/ScheduleValue').ScheduleValue } ScheduleValue
 */

/**
 * Component for selecting scheduling options in the Schedule step of the MultiMediaWizard.
 * @param {Object} props - The component props.
 * @param {ScheduleValue |null} props.currentValue - The currently selected scheduling option.
 * @param {function(ScheduleValue |null): void} props.onChange - Callback function to handle changes in the selected option.
 * @returns {JSX.Element} The rendered component.
 */
function ScheduleOptions({ currentValue, onChange }) {
  const isSelected = optionValue => {
    return Boolean(currentValue) === Boolean(optionValue); // both null or both non-null
  };

  /**
   * Helper function to create a new empty schedule value.
   * @returns {ScheduleValue} A new schedule object with default empty values.
   */
  const getEmptyValue = () => ({
    date: '',
    time: '',
    days: 0,
    recur: 0,
  });
  const makeCard = (optionKey, optionValue, title, description) => (
    <button
      type="button"
      className="scheduling-option"
      data-selected={isSelected(optionValue)}
      data-test={`schedule-option-${optionKey}`}
      onClick={() => onChange(optionValue)}
    >
      <span>{title}</span>
      <span className={`${MuiIcons.className} orange-button`}>
        {isSelected(optionValue) ? MuiIcons.icons.check_mark : MuiIcons.icons.invisible}
      </span>
      <span>{description}</span>
    </button>
  );

  return (
    <div className="scheduling-options">
      {makeCard(
        'create',
        currentValue || getEmptyValue(),
        'Create new schedule',
        'Inline editor for date, repeat, and day rules',
      )}
      {makeCard(
        'skip',
        null,
        'Skip schedule',
        'Allow immediate assignment and deployment without a schedule',
      )}
    </div>
  );
}

ScheduleOptions.defaultProps = {
  currentValue: null,
};

ScheduleOptions.propTypes = {
  currentValue: PropTypes.shape({
    date: PropTypes.string.isRequired, // YYYYMMDD
    time: PropTypes.string.isRequired, // HHMM
    days: PropTypes.number.isRequired, // bitmask for days of week
    recur: PropTypes.number.isRequired, // 0 = one off, 1 = daily, 2 = selected days of week
  }),
  onChange: PropTypes.func.isRequired,
};

export default ScheduleOptions;
